'use client';

import React, { memo, useState, useMemo, useCallback, useEffect, forwardRef, startTransition, useOptimistic } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/auth/auth-context'
import { usePermissions } from '@/hooks/usePermissions'
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Sheet, SheetContent, SheetTrigger} from "@/components/ui/sheet"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Package, BookA, CreditCard, Settings, LogOut, Calendar, MoreHorizontal, Users, LayoutDashboard } from 'lucide-react'
// MessageSquare

// Create a custom component that properly forwards refs to avoid nested asChild issues
const ProfileButton = forwardRef<HTMLButtonElement, {
  variant: "ghost"
  className: string
  "aria-label": string
  children: React.ReactNode
}>((props, ref) => (
  <Button ref={ref} {...props} />
))
ProfileButton.displayName = 'ProfileButton'

interface NavItem {
  href: string
  label: string
  icon: React.ElementType
  requiresPermission?: () => boolean
}

// Navigation items with permission-based access
const createNavItems = (permissions: ReturnType<typeof usePermissions>): NavItem[] => [
  { 
    href: '/dashboard', 
    label: 'Dashboard', 
    icon: LayoutDashboard, 
    requiresPermission: () => !!(permissions.canViewDashboard)
  },
  { 
    href: '/orders', 
    label: 'Orders', 
    icon: Package, 
    requiresPermission: () => {
      // Show orders page if user has ANY order-related permission
      return !!(permissions.canViewOrders || permissions.canCreateOrders || 
             permissions.canEditOrders || permissions.canDeleteOrders || permissions.canExportOrders);
    }
  },
  {
    href: '/payments',
    label: 'Payments',
    icon: CreditCard,
    requiresPermission: () => {
      // Show payments page if user has ANY payment-related permission
      return !!(permissions.canViewPayments || permissions.canManagePayments ||
             permissions.canProcessPayments || permissions.canViewPaymentHistory);
    }
  },
  {
    href: '/calendar',
    label: 'Calendar',
    icon: Calendar,
    requiresPermission: () => !!(permissions.canViewCalendar)
  },
  {
    href: '/customers',
    label: 'Customers',
    icon: Users,
    requiresPermission: () => {
      // Customers page shows orders and payments, so check both
      return !!(permissions.canViewOrders || permissions.canViewPayments ||
             permissions.canCreateOrders || permissions.canManagePayments);
    }
  },
  // {
  //   href: '/messages',
  //   label: 'Messages',
  //   icon: MessageSquare,
  //   requiresPermission: () => {
  //     // Show messages page if user has messages permissions
  //     return !!(permissions.canViewMessages);
  //   }
  // },
  {
    href: '/products',
    label: 'Products', 
    icon: BookA, 
    requiresPermission: () => {
      // Show products page if user has ANY product-related permission
      return !!(permissions.canViewProducts || permissions.canManageProducts || permissions.canDeleteProducts);
    }
  },
  {
    href: '/settings',
    label: 'Settings', 
    icon: Settings, 
    requiresPermission: () => {
      // Show settings page if user has ANY settings-related permission
      return !!(permissions.canViewSettings || permissions.canManageSettings || 
             permissions.canManageBilling);
    }
  },
]

// Loading skeleton component for desktop nav items
const DesktopMenuItemSkeleton = memo(() => (
  <div className="w-full mb-2 px-2">
    <div className="w-full h-10 bg-muted/50 rounded-md animate-pulse" />
  </div>
));

DesktopMenuItemSkeleton.displayName = 'DesktopMenuItemSkeleton';

// Loading skeleton component for mobile nav items
const MobileMenuItemSkeleton = memo(() => (
  <div className="flex flex-col items-center justify-center w-16 py-1">
    <div className="h-5 w-5 bg-muted/50 rounded animate-pulse mb-1" />
    <div className="h-3 w-8 bg-muted/50 rounded animate-pulse" />
  </div>
));

MobileMenuItemSkeleton.displayName = 'MobileMenuItemSkeleton';

// Memoized MenuItem component for desktop view
const DesktopMenuItem = memo(({ 
  item, 
  isActive, 
  isHovered,
  optimisticActive,
  onNavigate
}: { 
  item: NavItem, 
  isActive: boolean, 
  isHovered: boolean,
  optimisticActive: boolean,
  onNavigate: (href: string) => void
}) => {
  const Icon = item.icon;
  const [isPressing, setIsPressing] = useState(false);
  const displayActive = isActive || optimisticActive;
  
  const handleClick = useCallback(() => {
    onNavigate(item.href);
  }, [onNavigate, item.href]);

  const handleMouseDown = useCallback(() => {
    setIsPressing(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsPressing(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsPressing(false);
  }, []);
  
  if (!isHovered) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            className={cn(
              'w-full mb-2 transition-all duration-75 will-change-transform',
              displayActive ? 'bg-green-600 hover:bg-green-600/90 text-white' : 'hover:bg-green-600/10',
              'justify-center px-2',
              isPressing && 'scale-95 opacity-80'
            )}
            aria-label={item.label}
            aria-current={displayActive ? 'page' : undefined}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">{item.label}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }
  
  return (
    <Button
      variant="ghost"
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      className={cn(
        'w-full mb-2 transition-all duration-75 will-change-transform',
        displayActive ? 'bg-green-600 hover:bg-green-600/90 text-white' : 'hover:bg-green-600/10',
        'justify-start px-4',
        isPressing && 'scale-95 opacity-80'
      )}
      aria-current={displayActive ? 'page' : undefined}
    >
      <Icon className="h-4 w-4 mr-3" aria-hidden="true" />
      <span className="font-normal">{item.label}</span>
    </Button>
  );
});

DesktopMenuItem.displayName = 'DesktopMenuItem';

// Memoized MenuItem component for mobile view
const MobileMenuItem = memo(({ 
  item, 
  isActive,
  optimisticActive,
  onNavigate
}: { 
  item: NavItem, 
  isActive: boolean,
  optimisticActive: boolean,
  onNavigate: (href: string) => void
}) => {
  const Icon = item.icon;
  const [isPressing, setIsPressing] = useState(false);
  const displayActive = isActive || optimisticActive;
  
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    onNavigate(item.href);
  }, [onNavigate, item.href]);

  const handleTouchStart = useCallback(() => {
    setIsPressing(true);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsPressing(false);
  }, []);
  
  return (
    <button
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      className={cn(
        "flex flex-col items-center justify-center w-16 py-1 transition-all duration-75 will-change-transform",
        "text-muted-foreground hover:text-primary active:scale-95",
        displayActive && "text-green-600 hover:text-green-600",
        isPressing && "scale-95 opacity-80"
      )}
      aria-current={displayActive ? 'page' : undefined}
      aria-label={item.label}
    >
      <Icon className="h-5 w-5" aria-hidden="true" />
      <span className={cn("text-xs mt-1", displayActive && "font-semibold")}>{item.label}</span>
    </button>
  );
});

MobileMenuItem.displayName = 'MobileMenuItem';

// Memoized More Menu Item
const MoreMenuItem = memo(({
  item,
  isActive,
  optimisticActive,
  onItemClick,
  onNavigate
}: {
  item: NavItem,
  isActive: boolean,
  optimisticActive: boolean,
  onItemClick: () => void,
  onNavigate: (href: string) => void
}) => {
  const Icon = item.icon;
  const [isPressing, setIsPressing] = useState(false);
  const displayActive = isActive || optimisticActive;
  
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    onNavigate(item.href);
    onItemClick();
  }, [onNavigate, item.href, onItemClick]);

  const handleTouchStart = useCallback(() => {
    setIsPressing(true);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsPressing(false);
  }, []);
  
  return (
    <button
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      className={cn(
        "flex items-center w-full px-4 py-3 hover:bg-muted rounded-md transition-all duration-75 will-change-transform",
        displayActive && "bg-muted",
        isPressing && "scale-95 opacity-80"
      )}
      aria-current={displayActive ? 'page' : undefined}
    >
      <Icon className="h-5 w-5 mr-3" aria-hidden="true" />
      <span className="font-normal">{item.label}</span>
    </button>
  );
});

MoreMenuItem.displayName = 'MoreMenuItem';

function NavMenu() {
  const permissions = usePermissions();
  const [isHovered, setIsHovered] = useState(false)
  const [isMoreSheetOpen, setIsMoreSheetOpen] = useState(false)
  const pathname = usePathname()
  const router = useRouter()
  const { signOut, user } = useAuth()
  
  // Optimistic navigation state
  const [optimisticPath, setOptimisticPath] = useOptimistic(pathname)
  
  // Create navigation items based on permissions
  const navItems = useMemo(() => createNavItems(permissions), [permissions])
  
  // Aggressive prefetching - prefetch all routes immediately
  useEffect(() => {
    const routesToPrefetch = navItems
      .filter(item => !item.requiresPermission || item.requiresPermission())
      .map(item => item.href)
    
    // Prefetch each route immediately with high priority
    routesToPrefetch.forEach(route => {
      router.prefetch(route)
    })
  }, [router, navItems])
  
  // Additional aggressive prefetching for common routes
  useEffect(() => {
    const commonRoutes = ['/dashboard', '/orders', '/payments', '/customers', '/products']
    commonRoutes.forEach(route => {
      router.prefetch(route)
    })
  }, [router])
  
  // Memoize filtered nav items with optimized calculations
  const filteredNavItems = useMemo(() => {
    if (permissions.isLoading) {
      return []
    }
    
    // Use startTransition for heavy permission calculations
    return navItems.filter(item => {
      if (!item.requiresPermission) return true
      return item.requiresPermission()
    })
  }, [navItems, permissions.isLoading])
  
  // Optimized navigation handler with immediate visual feedback
  const handleNavigate = useCallback((href: string) => {
    // Immediate visual feedback - set optimistic state
    startTransition(() => {
      setOptimisticPath(href)
    })
    
    // Navigate in the background
    router.push(href)
  }, [router, setOptimisticPath])

  // Split nav items for mobile - show Dashboard, Orders, Payments + More
  const { visibleItems, moreItems } = useMemo(() => {
    const priorityRoutes = ['/dashboard', '/orders', '/payments', '/customers', '/products']
    const visible = filteredNavItems.filter(item => priorityRoutes.includes(item.href))
    const more = filteredNavItems.filter(item => !priorityRoutes.includes(item.href))
    return { visibleItems: visible, moreItems: more }
  }, [filteredNavItems])

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  return (
    <>
      {/* Desktop Navigation - Side Menu */}
      <div 
        className="md:block hidden fixed left-0 top-0 bottom-0 z-10 bg-background"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className={cn(
          "flex flex-col h-full transition-all duration-200 ease-in-out border-r",
          isHovered ? "w-64" : "w-16"
        )}>
          <TooltipProvider delayDuration={0}>
            <nav className="flex flex-col h-full">
              {/* Logo Section */}
              <div className="flex items-center p-4">
                <Link href='/orders' className="flex items-center justify-center">
                  <Image
                    src="/logo.png"
                    alt="MenuMate"
                    width={32}
                    height={32}
                    className="dark:invert"
                    priority
                  />
                </Link>
              </div>

              <div className="flex-grow overflow-y-auto">
                <div className="space-y-2 px-2 pt-4">
                  {permissions.isLoading ? (
                    // Show skeleton items while loading permissions
                    Array.from({ length: 6 }).map((_, index) => (
                      <DesktopMenuItemSkeleton key={`skeleton-${index}`} />
                    ))
                  ) : (
                    filteredNavItems.map((item) => (
                      <DesktopMenuItem 
                        key={item.href}
                        item={item}
                        isActive={item.href === '/' ? pathname === item.href : pathname.startsWith(item.href)}
                        optimisticActive={item.href === '/' ? optimisticPath === item.href : optimisticPath.startsWith(item.href)}
                        isHovered={isHovered}
                        onNavigate={handleNavigate}
                      />
                    ))
                  )}
                </div>
              </div>
              
              {/* User Profile */}
              <div className="mt-auto p-2 mb-4">
                <Popover>
                  {!isHovered ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                          <ProfileButton
                            variant="ghost"
                            className="w-full justify-center px-2 h-10"
                            aria-label="User profile"
                          >
                            <Avatar className="h-6 w-6">
                              <AvatarImage src="" alt={user?.email || 'User'} />
                              <AvatarFallback className="text-xs">
                                {user?.firstName?.charAt(0) || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                              </AvatarFallback>
                            </Avatar>
                            <span className="sr-only">User profile</span>
                          </ProfileButton>
                        </PopoverTrigger>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        Profile
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-full justify-start px-4 h-10"
                      >
                        <Avatar className="h-6 w-6 mr-3">
                          <AvatarImage src="" alt={user?.email || 'User'} />
                          <AvatarFallback className="text-xs">
                            {user?.firstName?.charAt(0) || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-normal truncate">{user?.email || 'User'}</span>
                      </Button>
                    </PopoverTrigger>
                  )}
                  <PopoverContent className="w-56 p-0" side="top" align="end">
                    <div className="space-y-2">
                      <div className="px-4 py-1.5">
                        <p className="text-sm font-medium">{user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'User'}</p>
                        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                      </div>
                      <div className="border-t p-2">
                        <Button
                          variant="ghost"
                          className="w-full justify-start px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={signOut}
                        >
                          <LogOut className="h-4 w-4 mr-2" aria-hidden="true" />
                          Log out
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </nav>
          </TooltipProvider>
        </div>
      </div>

      {/* Mobile Navigation - Bottom Menu */}
      <nav 
        className="md:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border h-24 px-4 z-50 pt-3"
        role="navigation"
        aria-label="Mobile navigation"
        style={{ position: 'fixed', transform: 'translateZ(0)' }}
      >
        <div className="flex items-start justify-around h-full">
          {permissions.isLoading ? (
            // Show skeleton items while loading permissions
            Array.from({ length: 3 }).map((_, index) => (
              <MobileMenuItemSkeleton key={`mobile-skeleton-${index}`} />
            ))
          ) : (
            <>
              {/* Visible Menu Items (first 4) */}
              {visibleItems.map((item) => (
                <MobileMenuItem
                  key={item.href}
                  item={item}
                  isActive={item.href === '/' ? pathname === item.href : pathname.startsWith(item.href)}
                  optimisticActive={item.href === '/' ? optimisticPath === item.href : optimisticPath.startsWith(item.href)}
                  onNavigate={handleNavigate}
                />
              ))}
              
              {/* More Button if there are additional items */}
              {moreItems.length > 0 && (
                <Sheet open={isMoreSheetOpen} onOpenChange={setIsMoreSheetOpen}>
                  <SheetTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex flex-col items-center justify-center w-16 py-1 h-auto text-muted-foreground hover:text-primary"
                      aria-label="More options"
                    >
                      <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
                      <span className="text-xs mt-1">More</span>
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="h-auto">
                    <div className="pb-6">
                      <h2 className="text-lg font-semibold mb-4">More Options</h2>
                      <div className="space-y-2">
                        {moreItems.map((item) => (
                          <MoreMenuItem
                            key={item.href}
                            item={item}
                            isActive={item.href === '/' ? pathname === item.href : pathname.startsWith(item.href)}
                            optimisticActive={item.href === '/' ? optimisticPath === item.href : optimisticPath.startsWith(item.href)}
                            onItemClick={() => setIsMoreSheetOpen(false)}
                            onNavigate={handleNavigate}
                          />
                        ))}
                        {/* Logout in More Menu */}
                        <button
                          onClick={() => {
                            signOut()
                            setIsMoreSheetOpen(false)
                          }}
                          className="flex items-center w-full px-4 py-3 hover:bg-muted rounded-md text-left"
                        >
                          <LogOut className="h-5 w-5 mr-3" aria-hidden="true" />
                          <span className="font-normal">Log out</span>
                        </button>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              )}
              
              {/* Mobile Profile Button (only show if no more items) */}
              {moreItems.length === 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex flex-col items-center justify-center w-16 py-1 h-auto text-muted-foreground hover:text-primary"
                  aria-label="User profile"
                >
                  <Avatar className="h-5 w-5">
                    <AvatarImage src="" alt={user?.email || 'User'} />
                    <AvatarFallback className="text-xs">
                      {user?.firstName?.charAt(0) || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs mt-1">Profile</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56" side="top" align="end">
                <div className="space-y-2">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'User'}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                  <div className="border-t pt-2">
                    <Button
                      variant="ghost"
                      className="w-full justify-start px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={signOut}
                    >
                      <LogOut className="h-4 w-4 mr-2" aria-hidden="true" />
                      Log out
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
              )}
            </>
          )}
        </div>
      </nav>
    </>
  )
}

export default memo(NavMenu)